<template>
  <div>
    <h1 class="text-2xl font-bold text-gray-900 mb-6">Clientes</h1>
    
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefone</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pedidos</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Gasto</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="customer in customers" :key="customer.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 font-medium text-gray-900">{{ customer.name }}</td>
            <td class="px-6 py-4 text-gray-500">{{ customer.phone }}</td>
            <td class="px-6 py-4">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {{ customer.total_orders }}
              </span>
            </td>
            <td class="px-6 py-4 font-medium">{{ formatCurrency(customer.total_spent) }}</td>
            <td class="px-6 py-4">
              <button @click="editCustomer(customer)" class="text-blue-600 hover:text-blue-800 font-medium mr-3">
                Editar
              </button>
              <button @click="deleteCustomer(customer)" class="text-red-600 hover:text-red-800 font-medium">
                Excluir
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <div v-if="customers.length === 0" class="p-8 text-center text-gray-500">
        Nenhum cliente cadastrado
      </div>
    </div>
    
    <!-- Edit Customer Modal -->
    <div v-if="showEditModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl p-6 w-full max-w-md mx-4">
        <h2 class="text-xl font-bold mb-4">Editar Cliente</h2>
        
        <form @submit.prevent="updateCustomer">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input v-model="customerForm.name" type="text" required class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
              <input v-model="customerForm.phone" type="tel" required class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input v-model="customerForm.email" type="email" class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
              <textarea v-model="customerForm.address" class="w-full border rounded-lg px-3 py-2" rows="2"></textarea>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
              <input v-model="customerForm.neighborhood" type="text" class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">CEP</label>
              <input v-model="customerForm.zip_code" type="text" class="w-full border rounded-lg px-3 py-2">
            </div>
          </div>
          
          <div class="flex justify-end gap-3 mt-6">
            <button type="button" @click="showEditModal = false" class="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
              Atualizar
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores'
import { customerApi } from '@/utils/api'
import { formatCurrency } from '@/utils/formatters'
import { notifyError, notifySuccess } from '@/utils/notifications'
import { confirmDialog } from '@/composables/useConfirm'
import type { Customer } from '@/types'

const authStore = useAuthStore()

const customers = ref<Customer[]>([])
const showEditModal = ref(false)
const editingCustomerId = ref<number | null>(null)

const customerForm = ref({
  name: '',
  phone: '',
  email: '',
  address: '',
  neighborhood: '',
  zip_code: ''
})

const loadCustomers = async () => {
  const companyId = authStore.companyId || 1
  try {
    const res = await customerApi.getByCompany(companyId)
    customers.value = res.data
  } catch (error) {
    console.error('Erro ao carregar clientes:', error)
  }
}

const editCustomer = (customer: Customer) => {
  editingCustomerId.value = customer.id
  customerForm.value = {
    name: customer.name,
    phone: customer.phone,
    email: customer.email || '',
    address: customer.address || '',
    neighborhood: customer.neighborhood || '',
    zip_code: customer.zip_code || ''
  }
  showEditModal.value = true
}

const updateCustomer = async () => {
  if (!editingCustomerId.value) return
  
  try {
    await customerApi.update(editingCustomerId.value, customerForm.value)
    showEditModal.value = false
    editingCustomerId.value = null
    loadCustomers()
    notifySuccess('Cliente atualizado com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao atualizar cliente')
  }
}

const deleteCustomer = async (customer: Customer) => {
  if (!await confirmDialog({ title: 'Confirmar exclusão', message: `Tem certeza que deseja excluir o cliente "${customer.name}"?`, danger: true, confirmLabel: 'Excluir' })) return
  
  try {
    await customerApi.delete(customer.id)
    loadCustomers()
    notifySuccess('Cliente excluído com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao excluir cliente')
  }
}

onMounted(() => {
  loadCustomers()
})
</script>
